import { Box } from "@chakra-ui/react";
import React from "react";

import type { TokenInfo } from "types/api/token";

import useIsMobile from "lib/hooks/useIsMobile";
import useIsMounted from "lib/hooks/useIsMounted";
import AddressCsvExportLink from "ui/address/AddressCsvExportLink";
import ActionBar from "ui/shared/ActionBar";
import DataFetchAlert from "ui/shared/DataFetchAlert";
import DataListDisplay from "ui/shared/DataListDisplay";
import Pagination from "ui/shared/pagination/Pagination";
import type { QueryWithPagesResult } from "ui/shared/pagination/useQueryWithPages";

import TokenHoldersList from "./TokenHoldersList";
import TokenHoldersTable from "./TokenHoldersTable";

const TABS_HEIGHT = 88;

type Props = {
  token?: TokenInfo;
  holdersQuery: QueryWithPagesResult<"general:token_holders">;
  shouldRender?: boolean;
  tabsHeight?: number;
};

const TokenHoldersContent = ({
  holdersQuery,
  token,
  shouldRender = true,
  tabsHeight = TABS_HEIGHT,
}: Props) => {
  const isMobile = useIsMobile();
  const isMounted = useIsMounted();

  if (!isMounted || !shouldRender) {
    return null;
  }

  if (holdersQuery.isError) {
    return <DataFetchAlert />;
  }

  const actionBar = isMobile && holdersQuery.pagination.isVisible && (
    <ActionBar mt={-6}>
      {token && (
        <AddressCsvExportLink
          address={token.address_hash}
          params={{ type: "holders" }}
          isLoading={holdersQuery.pagination.isLoading}
        />
      )}
      <Pagination ml="auto" {...holdersQuery.pagination} />
    </ActionBar>
  );

  const items = holdersQuery.data?.items;

  const content =
    items && token ? (
      <>
        <Box display={{ base: "none", lg: "block" }}>
          <TokenHoldersTable
            data={items}
            token={token}
            top={tabsHeight}
            isLoading={holdersQuery.isPlaceholderData}
          />
        </Box>
        <Box display={{ base: "block", lg: "none" }}>
          <TokenHoldersList
            data={items}
            token={token}
            isLoading={holdersQuery.isPlaceholderData}
          />
        </Box>
      </>
    ) : null;

  return (
    <DataListDisplay
      isError={holdersQuery.isError}
      itemsNum={holdersQuery.data?.items.length}
      emptyText="There are no holders for this token."
      actionBar={actionBar}
    >
      {content}
    </DataListDisplay>
  );
};

export default TokenHoldersContent;
